import React, {
  memo,
  useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  ButtonGroup,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogContent,
  Popover,
  Stack,
  Button,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faEllipsisH,
  faCheck,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../providers/authProvider';
import { ErrorDiv } from './styles';
import { ItemField, valuePropType } from './itemField';
import api from '../../api/req';
import { debounce } from '../../api/utils';
// eslint-disable-next-line import/no-cycle
import {
  getFrontEndByBackend,
  getSelectorByBackend,
} from '../../const/meta';

const ITEM_HEIGHT = 48;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  height: 15px;
`;
function ItemPicker({
  value,
  canErase,
  onChange,
  backend,
  noBorder,
  noBackground,
  filter,
  disabled,
  id,
  margin,
}) {
  const Selector = useMemo(
    () => getSelectorByBackend(backend),
    [backend],
  );
  const frontEnd = useMemo(
    () => getFrontEndByBackend(backend),
    [backend],
  );
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [listOpened, setListOpened] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [editText, setEditText] = useState(null);
  const [items, setItems] = useState([]);

  const inputRef = useRef();
  const controller = useRef(null);

  const { auth } = useContext(AppContext);

  const search = useCallback(
    (parttext) => {
      const loader = async () => {
        if (controller.current) controller.current.abort();
        controller.current = new AbortController();
        const r = await api.get(backend, auth, {
          ...filter,
          search: parttext,
        }, controller.current);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const d = await r.json();
        if ('results' in d) return d.results;
        return d;
      };
      setLoading(true);
      setErr(null);
      loader()
        .then((data) => {
          setItems(data);
          setListOpened(!!data.length);
          setLoading(false);
        })
        .catch((e) => {
          if (!(e instanceof DOMException && e.name === 'AbortError')) {
            setLoading(false);
            setErr(e.message);
          }
        });
    },
    [auth, backend, filter],
  );

  const onSearch = useMemo(
    () => debounce(search),
    [search],
  );

  const [selectedItem, setSelectedItem] = useState(value);

  const onChoice = useCallback(
    (e, v) => {
      onChange(e, v);
      setModalOpened(false);
      setEditText(null);
    },
    [onChange],
  );
  const onOpen = useCallback(
    () => {
      if (value && value.id) {
        history.push(`${frontEnd}${value.id}/`);
      }
    },
    [frontEnd, history, value],
  );

  return (
    <>
      <ItemField
        id={id}
        value={value}
        ref={inputRef}
        editText={editText}
        onChange={(e, v) => {
          setEditText(v);
          onSearch(v);
        }}
        onBlur={() => {
          setEditText(null);
          setListOpened(false);
        }}
        noBorder={noBorder}
        noBackground={noBackground}
        readOnly={disabled}
        endAdornment={(
          <>
            {loading && <CircularProgress size="1rem" />}
            <ButtonGroup size="small" variant="text">
              <IconButton
                onClick={() => {
                  setModalOpened(true);
                }}
                disabled={disabled}
                size="small"
                title="Обрати"
              >
                <StyledFontAwesomeIcon icon={faEllipsisH} size="sm" transform="down-6" />
              </IconButton>
              <IconButton onClick={onOpen} disabled={!value || !value.id} size="small" title="Відкрити">
                <StyledFontAwesomeIcon icon={faUpRightFromSquare} size="sm" />
              </IconButton>
              {canErase && (
                <IconButton
                  onClick={(e) => onChange(e, {
                    id: null,
                    repr: '',
                  })}
                  disabled={disabled}
                >
                  <StyledFontAwesomeIcon icon={faTimes} size="sm" />
                </IconButton>
              )}
            </ButtonGroup>
          </>
        )}
        errored={!!err}
        margin={margin}
      />
      {err ? (<ErrorDiv>{err}</ErrorDiv>) : null}
      <Popover
        anchorEl={inputRef.current}
        open={listOpened}
        onClose={() => setListOpened(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 4.5,
            width: '60ch',
          },
        }}
        TransitionProps={{
          mountOnEnter: true,
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        {items.map((item) => (
          <MenuItem
            key={item.id}
            selected={value && item.id === value.id}
            onClick={(e) => {
              setListOpened(false);
              setEditText(null);
              onChange(e, item);
            }}
          >
            {item.repr}
          </MenuItem>
        ))}
      </Popover>
      <Dialog
        onClose={() => setModalOpened(false)}
        open={modalOpened}
        fullScreen
        // maxWidth="xl"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            background: 'white', p: 2, borderRadius: 1, mb: 2, boxShadow: 1,
          }}
        >
          <ButtonGroup>
            <Button
              startIcon={<FontAwesomeIcon icon={faCheck} />}
              color="success"
              onClick={(e) => onChoice(e, selectedItem)}
              disabled={!selectedItem}
            >
              Обрати
            </Button>
            <Button
              startIcon={<FontAwesomeIcon icon={faTimes} />}
              color="error"
              onClick={() => setModalOpened(false)}
            >
              Скасувати
            </Button>
          </ButtonGroup>
        </Stack>
        <DialogContent>
          <Selector
            filter={filter}
            currentId={value && value.id}
            onChoice={onChoice}
            onSelect={(e, v) => setSelectedItem(v)}
          />
        </DialogContent>
      </Dialog>
    </>

  );
}
ItemPicker.propTypes = {
  id: PropTypes.string,
  value: valuePropType,
  canErase: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  backend: PropTypes.string.isRequired,
  noBorder: PropTypes.bool,
  noBackground: PropTypes.bool,
  filter: PropTypes.shape({}),
  disabled: PropTypes.bool,
  margin: PropTypes.oneOf(['none', 'normal', 'dense']),
};

ItemPicker.defaultProps = {
  id: null,
  value: {
    id: null,
    repr: '',
  },
  canErase: false,
  noBorder: false,
  noBackground: false,
  filter: null,
  disabled: false,
  margin: 'dense',
};

export default memo(ItemPicker);
